const EVENT_CATS = ["music", "festivals", "family", "business", "networking", "food-drink", "sports", "community", "arts", "tourism", "wellness"];
const EXP_CATS = ["food-tours", "hidden-gems", "beach", "surfing", "photography", "hinterland", "wellness", "family", "nightlife", "luxury"];
const BIZ_CATS = ["restaurants", "cafes", "bars", "attractions", "wellness", "fitness", "beauty", "professional", "tourism", "retail"];
const GUIDE_CATS = ["weekend", "beaches", "restaurants", "family", "school-holidays", "hidden-gems", "romantic", "adventure", "wellness"];

export const SECTIONS = {
  events: {
    label: "What's On",
    singular: "event",
    titleKey: "title",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "category", label: "Category", type: "select", options: EVENT_CATS, required: true },
      { key: "start_date", label: "Start date & time", type: "datetime", required: true },
      { key: "end_date", label: "End date & time (optional, multi-day)", type: "datetime" },
      { key: "venue", label: "Venue", type: "text", required: true },
      { key: "address", label: "Address", type: "text", required: true },
      { key: "suburb", label: "Suburb", type: "text", required: true },
      { key: "short_description", label: "Short blurb (card)", type: "text" },
      { key: "description", label: "Full description", type: "textarea", required: true },
      { key: "image", label: "Cover image", type: "image", required: true },
      { key: "gallery", label: "Photo gallery (optional)", type: "gallery" },
      { key: "price_mode", label: "Ticket pricing", type: "select", options: ["price", "free", "see_link"], labels: { price: "Set a price (AUD)", free: "Free", see_link: "See link for ticket price" } },
      { key: "price_from", label: "Price from (AUD) — only used when 'Set a price' is chosen", type: "number" },
      { key: "booking_url", label: "Book / find out more link", type: "url" },
      { key: "recurrence", label: "Repeats", type: "select", options: ["none", "weekly", "fortnightly", "monthly"] },
      { key: "recurrence_until", label: "Stop repeating after (optional)", type: "date" },
      { key: "organizer", label: "Organiser", type: "text" },
      { key: "tags", label: "Tags (comma separated)", type: "tags" },
      { key: "featured", label: "Feature on homepage", type: "bool" },
      { key: "published", label: "Published (untick to keep as a hidden draft)", type: "bool" },
    ],
    defaults: { price_from: 0, price_mode: "price", featured: false, published: true, recurrence: "none", organizer: "Hey GC", tags: [], gallery: [], currency: "AUD" },
  },
  experiences: {
    label: "Experiences",
    singular: "experience",
    titleKey: "title",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "category", label: "Category", type: "select", options: EXP_CATS, required: true },
      { key: "location", label: "Location", type: "text", required: true },
      { key: "duration_hours", label: "Duration (hours)", type: "number", required: true },
      { key: "price", label: "Price per person (AUD)", type: "number", required: true },
      { key: "max_guests", label: "Max guests", type: "number" },
      { key: "short_description", label: "Short blurb (card)", type: "text" },
      { key: "description", label: "Full description", type: "textarea", required: true },
      { key: "image", label: "Cover image", type: "image", required: true },
      { key: "gallery", label: "Photo gallery (optional)", type: "gallery" },
      { key: "booking_url", label: "Book / find out more link", type: "url" },
      { key: "host.name", label: "Host name", type: "text", required: true },
      { key: "host.bio", label: "Host bio", type: "textarea", required: true },
      { key: "host.avatar", label: "Host photo", type: "image", required: true },
      { key: "inclusions", label: "Inclusions (comma separated)", type: "tags" },
      { key: "highlights", label: "Highlights (comma separated)", type: "tags" },
      { key: "featured", label: "Feature on homepage", type: "bool" },
      { key: "published", label: "Published (untick to keep as a hidden draft)", type: "bool" },
    ],
    defaults: { max_guests: 8, featured: false, published: true, inclusions: [], highlights: [], gallery: [], host: { name: "", bio: "", avatar: "", specialties: [] } },
  },
  businesses: {
    label: "Tried & Tested",
    singular: "business listing",
    titleKey: "name",
    fields: [
      { key: "name", label: "Business name", type: "text", required: true },
      { key: "category", label: "Category", type: "select", options: BIZ_CATS, required: true },
      { key: "address", label: "Address", type: "text", required: true },
      { key: "suburb", label: "Suburb", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea", required: true },
      { key: "image", label: "Cover image", type: "image", required: true },
      { key: "gallery", label: "Photo gallery (optional)", type: "gallery" },
      { key: "price_range", label: "Price range", type: "select", options: ["$", "$$", "$$$", "$$$$"] },
      { key: "phone", label: "Phone", type: "text" },
      { key: "website", label: "Website", type: "url" },
      { key: "instagram", label: "Instagram handle", type: "text" },
      { key: "hours", label: "Opening hours", type: "text" },
      { key: "tags", label: "Tags (comma separated)", type: "tags" },
      { key: "featured", label: "Feature on homepage", type: "bool" },
      { key: "published", label: "Published (untick to keep as a hidden draft)", type: "bool" },
    ],
    defaults: { price_range: "$$", featured: false, published: true, tags: [], gallery: [] },
  },
  guides: {
    label: "The Local Scoop",
    singular: "guide",
    titleKey: "title",
    fields: [
      { key: "title", label: "Title", type: "text", required: true },
      { key: "subtitle", label: "Subtitle", type: "text" },
      { key: "category", label: "Category", type: "select", options: GUIDE_CATS, required: true },
      { key: "excerpt", label: "Excerpt (card)", type: "text", required: true },
      { key: "content", label: "Article content", type: "textarea", rows: 12, required: true },
      { key: "cover_image", label: "Cover image", type: "image", required: true },
      { key: "author", label: "Author", type: "text" },
      { key: "read_time_min", label: "Read time (min)", type: "number" },
      { key: "tags", label: "Tags (comma separated)", type: "tags" },
      { key: "featured", label: "Feature on homepage", type: "bool" },
      { key: "published", label: "Published (untick to keep as a hidden draft)", type: "bool" },
    ],
    defaults: { author: "Hey GC Editorial", read_time_min: 5, featured: false, published: true, tags: [] },
  },
  ads: {
    label: "Sponsored ads",
    singular: "sponsored ad",
    titleKey: "sponsor_name",
    fields: [
      { key: "sponsor_name", label: "Sponsor / business name", type: "text", required: true },
      { key: "logo", label: "Sponsor logo (square works best)", type: "image" },
      { key: "tagline", label: "Tagline (handwritten style, optional)", type: "text" },
      { key: "headline", label: "Headline", type: "text", required: true },
      { key: "body", label: "Body copy (1–3 sentences)", type: "textarea" },
      { key: "cta_label", label: "Button text", type: "text" },
      { key: "cta_url", label: "Button link (https://…)", type: "url", required: true },
      { key: "footnote", label: "Small footnote next to button (optional)", type: "text" },
      { key: "tiles", label: "Tiles", type: "tiles" },
      { key: "start_date", label: "Start showing from (optional)", type: "date" },
      { key: "end_date", label: "Stop showing after (optional)", type: "date" },
      { key: "published", label: "Live (untick to pause this ad)", type: "bool" },
    ],
    defaults: { cta_label: "Find out more", published: true, tiles: [], clicks: 0, impressions: 0 },
  },
};

export const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

export const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const out = { ...obj };
  let cur = out;
  keys.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[keys[keys.length - 1]] = value;
  return out;
};
